import { fromPairs, intersection, mapValues, pull } from 'lodash';
import { flatten, groupBy, isEmpty, isEqual, sortBy, uniq } from 'lodash/fp';
import {
    AgeGroupEntity,
    ChartProviderFilterValue,
    ChartProviderFilters,
    ChartType,
    DataCondition,
    DataGranularity,
    DataGranularityKey,
    DataKey,
    DataRecord,
    DataResponse,
    DataType,
    TimeUnitKey,
} from 'types';
import { DeepReadonly } from 'utility-types';

import { populationMeasureId } from '../config';
import _ from '../locale';
import { getAgeGroupsRange } from '../models/age-group';
import { CHARTS_NON_SUPPORTED_DATA_TYPES, TYPE_SCATTER_MAP_CHART } from '../models/chart-type';
import {
    AGE_GROUP_ID_KEY,
    ANTIBIOTIC_CLASS_ID_KEY,
    CASE_HOSPITALIZATION_ID_KEY,
    CASE_OUTCOME_ID_KEY,
    CASE_SEVERITY_ID_KEY,
    CAUSE_ID_KEY,
    COMORBID_CAUSE_ID_KEY,
    COUNTERFACTUAL_ID_KEY,
    COVARIATE_ID_KEY,
    DATA_TYPE_KEY,
    DATE_KEY,
    EDUCATION_ID_KEY,
    FORECAST_SCENARIO_ID_KEY,
    GENDER_ID_KEY,
    INFECTION_TYPE_ID_KEY,
    INFECTIOUS_SYNDROME_ID_KEY,
    LOCATION_ID_KEY,
    LOWER_KEY,
    MEASURE_ID_KEY,
    METRIC_ID_KEY,
    PATHOGEN_ID_KEY,
    PAYER_ID_KEY,
    PRIMARY_ENTITY_ID_KEY,
    PRIMARY_ENTITY_ID_LOCALIZATION_KEY,
    RACE_ID_KEY,
    REI_ID_KEY,
    RESPONSE_ID_KEY,
    RISK_EXPOSURE_ID_KEY,
    ROUND_ID_KEY,
    SDOH_ID_KEY,
    SEQUELA_ID_KEY,
    SEROTYPE_ID_KEY,
    TOC_ID_KEY,
    UNDERLYING_CONDITION_KEY,
    UPPER_KEY,
    VACCINATION_STATUS_ID_KEY,
    WOLBACHIA_INCLUDED_ID_KEY,
    getTimeUnitKeys,
} from '../models/data-key';
import {
    AntimicrobialResistanceRecords,
    CauseAssociatedComorbidityStudyCoefficients,
    CauseOutcome,
    Covid19Records,
    HealthThreatsMetricsRecords,
    ImpairmentRecords,
    InjuryRecords,
    LifeExpectancyRecords,
    PopulationRecords,
    RiskExposureRecords,
    SequelaOutcome,
    SevRecords,
    TakedaDengueRecords,
    areComorbidityCoprevalenceDataTypes,
    areComorbidityDataTypes,
    areReiDataTypes,
    dataTypeHasPrimaryEntity,
    getDataTypeLocalePrefix,
    getDataTypePrimaryEntityKey,
} from '../models/data-type';
import { formatValue } from './filters-helpers';

const PAST_ESTIMATE_SCENARIO_ID = 0;

export const getForecastScenarioIds = ({ columns, records }) =>
    Object.keys(
        groupBy((record) => record[columns.indexOf(FORECAST_SCENARIO_ID_KEY)]),
        records
    )
        .map(Number)
        .sort();

export const getDataResponseWithBaseForecastData = (
    { columns, records },
    baseForecastScenarioId: number
) => {
    const forecastScenarioIdx = columns.indexOf(FORECAST_SCENARIO_ID_KEY);

    return {
        columns,
        records: records.filter(
            (record) =>
                record[forecastScenarioIdx] === PAST_ESTIMATE_SCENARIO_ID ||
                record[forecastScenarioIdx] === baseForecastScenarioId
        ),
    };
};

export const filterForecastingData = (
    response: DataResponse,
    pastEstimateScenarioId: number
): DataResponse => {
    const { columns, records } = response;
    const forecastScenarioIdx = columns.indexOf(FORECAST_SCENARIO_ID_KEY);

    return {
        columns,
        records: records.filter((record) => record[forecastScenarioIdx] === pastEstimateScenarioId),
    };
};

export const splitForecastData = (
    response: DataResponse,
    observedForecastScenarioId: number
): { observedData: DataResponse; forecastData: DataResponse } => {
    const { columns, records } = response;

    const forecastScenarioIdx = columns.indexOf(FORECAST_SCENARIO_ID_KEY);

    const observedData = {
        columns,
        records: records.filter(
            (record) => record[forecastScenarioIdx] === observedForecastScenarioId
        ),
    };

    const forecastData = {
        columns,
        records: records.filter(
            (record) => record[forecastScenarioIdx] !== observedForecastScenarioId
        ),
    };

    return { observedData, forecastData };
};

export const connectPastEstimateWithBaseForecast = (
    { columns, records },
    pastEstimateScenarioId,
    baseForecastScenarioId,
    timeUnitKey,
    multipleFilterKeys
) => {
    const timeUnitIdx = columns.indexOf(timeUnitKey);
    const forecastScenarioIdx = columns.indexOf(FORECAST_SCENARIO_ID_KEY);
    const upperIdx = columns.indexOf(UPPER_KEY);
    const lowerIdx = columns.indexOf(LOWER_KEY);

    const castTimeUnitToNumber = (value) =>
        timeUnitKey === DATE_KEY ? new Date(value).getTime() : value;

    const getRecordsTimeUnits = (source) =>
        uniq(source.map((record) => record[timeUnitIdx])).sort(
            (a, b) => castTimeUnitToNumber(a) - castTimeUnitToNumber(b)
        );

    const filterRecordsByValue = (
        source: DataRecord[],
        columnIdx: number,
        value: number
    ): DataRecord[] => source.filter((record) => record[columnIdx] === value);

    const pastEstimateRecords = filterRecordsByValue(
        records,
        forecastScenarioIdx,
        pastEstimateScenarioId
    );
    const forecastRecords = records.filter(
        (record) => record[forecastScenarioIdx] !== pastEstimateScenarioId
    );

    if (!(pastEstimateRecords.length && forecastRecords.length)) {
        // nothing to connect
        return { columns, records };
    }

    const pastEstimateTimeUnits = getRecordsTimeUnits(pastEstimateRecords);
    const prevToForecastTimeUnitStart = pastEstimateTimeUnits[pastEstimateTimeUnits.length - 1];
    const prevToForecastTimeUnitStartRecords = filterRecordsByValue(
        pastEstimateRecords,
        timeUnitIdx,
        prevToForecastTimeUnitStart
    );

    const forecastTimeUnits = getRecordsTimeUnits(forecastRecords);
    const forecastTimeUnitStart = forecastTimeUnits[0];
    const forecastTimeUnitStartRecords = filterRecordsByValue(
        forecastRecords,
        timeUnitIdx,
        forecastTimeUnitStart
    );

    const multipleFilterKeysIdx = multipleFilterKeys.map((key) => columns.indexOf(key));
    const groupedForecastingRecords = groupBy(
        (record) => multipleFilterKeysIdx.map((idx) => record[idx]).join('_'),
        forecastTimeUnitStartRecords
    );

    const keysToCompare = multipleFilterKeys.filter((key) => key !== FORECAST_SCENARIO_ID_KEY);
    const idxsToCompare = keysToCompare.map((key) => columns.indexOf(key));

    const missingRecords = (Object.values(groupedForecastingRecords) || [])
        .map((o) => {
            const record = o[0];
            const correspondingPastEstimateRecord = prevToForecastTimeUnitStartRecords.find(
                (pastEstimateRecord) =>
                    idxsToCompare.every((idx) => record[idx] === pastEstimateRecord[idx])
            );

            if (!correspondingPastEstimateRecord) {
                return null;
            }

            const correspondingPastEstimateRecordCopy = [...correspondingPastEstimateRecord];

            if (record[upperIdx] === record[lowerIdx] && record[upperIdx] === null) {
                correspondingPastEstimateRecordCopy[upperIdx] = null;
                correspondingPastEstimateRecordCopy[lowerIdx] = null;
            }

            correspondingPastEstimateRecordCopy[forecastScenarioIdx] = record[forecastScenarioIdx];
            return correspondingPastEstimateRecordCopy;
        })
        .filter((record) => record !== null);

    return {
        columns,
        records: sortBy(
            (record) => castTimeUnitToNumber(record[timeUnitIdx]),
            [...records, ...missingRecords]
        ),
    };
};

export const localizeDataResponses = (
    dataResponses: Record<string, DataResponse>,
    _: (key: string) => string
): Record<
    string,
    DeepReadonly<{
        columns: DataKey[];
        records: (string | number)[][];
    }>
> =>
    mapValues(dataResponses, (response, dataType) => ({
        columns: response.columns,
        records: response.records.map((record) => {
            const primaryEntityKey = getDataTypePrimaryEntityKey(dataType);
            const primaryEntityIndex = response.columns.indexOf(primaryEntityKey);

            const translatedRecord = record.map((value, idx) => {
                const prefix =
                    idx === primaryEntityIndex ? primaryEntityKey : response.columns[idx];

                return formatValue(prefix, value, _, { metric_id: 'long_metric_' }, dataType);
            });

            return translatedRecord;
        }),
    }));

export const localizeColumn = (column: DataKey, dataTypes, _: (key: string) => string): string => {
    const isPrimaryEntityKey = column === PRIMARY_ENTITY_ID_LOCALIZATION_KEY;
    const isComorbidityData = areComorbidityDataTypes(dataTypes);
    const isAMRData = dataTypes.includes(AntimicrobialResistanceRecords);

    let localizationKey = column;
    if (isPrimaryEntityKey) {
        if (isComorbidityData) {
            localizationKey = UNDERLYING_CONDITION_KEY;
        } else if (isAMRData) {
            localizationKey = PATHOGEN_ID_KEY;
        }
    }

    return _(`filter_label_${localizationKey}`);
};

export const localizeColumns = (
    columns: Array<DataKey>,
    dataTypes,
    _: (key: string) => string
): string[] => columns.map((column) => localizeColumn(column, dataTypes, _));

export const localizeRecords = (
    columns: Array<DataKey | typeof DATA_TYPE_KEY>,
    records: DataRecord,
    // @todo: try to import localizer
    _: (key: string) => string
): DataRecord[] => {
    if (!(columns && columns.length && records && records.length)) {
        return [];
    }
    return records.map((record: DataRecord) => localizeRecord(record, columns));
};

export const localizeRecord = (
    record: DataRecord,
    columns: Array<DataKey | typeof DATA_TYPE_KEY>
): DataRecord => {
    const dataTypeIdx = columns.indexOf(DATA_TYPE_KEY);
    const dataType = record[dataTypeIdx];

    return record.map((value, idx) => localizeRecordValue(value, columns[idx], dataType));
};

export const localizeRecordValue = (
    value: string | number,
    granularityKey: string,
    dataType: DataType
) => {
    return formatValue(granularityKey, value, _, { metric_id: 'long_metric_' }, dataType);
};

export const mergeAndSortDataResponses = (
    data: Record<string, DataResponse> | null
): DataResponse => {
    if (!data) {
        return { columns: [], records: [] };
    }

    const mergedDataResponses = mergeDataResponses(data);

    const timeUnitKeys = intersection(
        getTimeUnitKeys(),
        mergedDataResponses.columns
    ) as TimeUnitKey[];

    if (!(timeUnitKeys && timeUnitKeys.length)) {
        return mergedDataResponses;
    }

    return sortResponseByTimeUnit(mergedDataResponses, timeUnitKeys[0]);
};

export const mergeDataResponses = (data: Record<string, DataResponse> | null): DataResponse => {
    if (!data) {
        return { columns: [], records: [] };
    }

    const responsesWithDataTypes = addDataTypeToResponses(data);

    return {
        columns: mergeDataColumns(responsesWithDataTypes),
        records: mergeDataRecords(responsesWithDataTypes),
    };
};

// @deprecated
export const getFirstSupportedDataResponse = (
    data: Record<string, DataResponse>,
    chartType?: ChartType
): DataResponse => {
    const emptyResponse = { columns: [], records: [] };
    if (!data) {
        return emptyResponse;
    }
    const responses = Object.entries(data);
    if (!responses?.length) {
        return emptyResponse;
    }

    const supportedDataResponses = responses
        .filter(
            ([dataType]) =>
                !chartType || !(CHARTS_NON_SUPPORTED_DATA_TYPES[chartType] || []).includes(dataType)
        )
        .map(([dataType, dataResponse]) => dataResponse);

    return supportedDataResponses?.[0] || emptyResponse;
};

export const addDataTypeToResponses = (data: Record<string, DataResponse>) => {
    if (!data) {
        return null;
    }

    const responses = Object.entries(data);

    const responsesWithDataType = responses.map(([key, value]) => [
        key,
        addDataTypeToResponse(value, key),
    ]);

    return fromPairs(responsesWithDataType);
};

export const addDataTypeToResponse = (response: DataResponse, dataType: DataType) => {
    const { columns, records } = response;

    const columnsWithDataType = addDataTypeToColumns(columns, dataType);
    const recordsWithDataType = addDataTypeToRecords(columns, records, dataType);

    return {
        columns: columnsWithDataType,
        records: recordsWithDataType,
    };
};

const addDataTypeToColumns = (columns: ReadonlyArray<DataKey>, dataType: DataType) =>
    [PRIMARY_ENTITY_ID_LOCALIZATION_KEY, columns[0], DATA_TYPE_KEY, ...columns.slice(1)].map(
        (filterKey) =>
            filterKey === getDataTypePrimaryEntityKey(dataType) ? PRIMARY_ENTITY_ID_KEY : filterKey
    );

const addDataTypeToRecords = (
    columns: DeepReadonly<
        (
            | DataKey
            | typeof PRIMARY_ENTITY_ID_KEY
            | typeof DATA_TYPE_KEY
            | typeof PRIMARY_ENTITY_ID_LOCALIZATION_KEY
        )[]
    >,
    records: DeepReadonly<DataRecord[]>,
    dataType: DataType
) => {
    const primaryEntityIdIdx = columns.indexOf(getDataTypePrimaryEntityKey(dataType));
    const hasPrimaryEntity = dataTypeHasPrimaryEntity(dataType);
    const localePrefix = getDataTypeLocalePrefix(dataType);

    return records.map((record: DeepReadonly<DataRecord>) => [
        `${localePrefix}${hasPrimaryEntity ? record[primaryEntityIdIdx] : 1}`,
        record[0],
        dataType,
        ...record.slice(1),
    ]);
};

export const mergeDataRecords = (data: Record<string, DataResponse>) => {
    return flatten(Object.values(data).map((i) => i?.records));
};

export const mergeDataColumns = (data: Record<string, DataResponse>) => {
    const responses = Object.entries(data);

    // check if columns of multiple responses are compatible
    if (responses.length > 1) {
        const incompatible = responses.some(([type, res], idx) => {
            const columns = res.columns.filter((i) => i !== getDataTypePrimaryEntityKey(type));
            responses
                .filter((i, _idx) => _idx !== idx)
                .forEach(([_type, _res]) => {
                    const _columns = _res.columns.filter(
                        (i) => i !== getDataTypePrimaryEntityKey(_type)
                    );
                    return !isEqual(columns, _columns);
                });
        });

        if (incompatible) {
            throw Error('Columns are different when merging data responses');
        }
    }
    const firstResponse = Object.values(data)[0];
    return firstResponse != null ? firstResponse.columns : [];
};

// @todo: delete
export const localizeConditions = (conditions: DataCondition[]) =>
    conditions.map(localizeCondition).join(', ');

// @todo: delete
export const localizeCondition = (condition: DataCondition) =>
    _(getDataTypeLocalePrefix(condition.data_type) + condition.primary_entity_id);

export const mapConditionToValue = (condition: DataCondition): string =>
    condition.data_type + '::' + condition.primary_entity_id;

export const mapValueToCondition = (value: string): DataCondition => {
    const [data_type, id] = value.split('::');
    return { data_type, primary_entity_id: parseInt(id) };
};

export const localizeConditionValue = (value: string): string =>
    localizeCondition(mapValueToCondition(value));

export const mergeCauseAndSequelaHierarchy = (causeHierarchy, sequelaHierarchy) => {
    const mergedHierarchy = [
        ...causeHierarchy.map((cause) => ({
            ...cause,
            data_type: CauseOutcome,
        })),
    ];

    sequelaHierarchy.forEach((sequela) => {
        const { parent_cause_id } = sequela;

        const parentCause = causeHierarchy.find((cause) => cause.id === parent_cause_id);

        if (parentCause) {
            const {
                level,
                level0_parent_id,
                level1_parent_id,
                level2_parent_id,
                level3_parent_id,
            } = parentCause;

            const causeLocalePrefix = getDataTypeLocalePrefix(CauseOutcome);

            mergedHierarchy.push({
                id: sequela.id,
                data_type: SequelaOutcome,
                level: level + 1,
                level0_parent_id,
                level1_parent_id,
                level2_parent_id,
                level3_parent_id,
                [`level${level}_parent_id`]: `${causeLocalePrefix}${parent_cause_id}`,
            });
        }
    });

    return mergedHierarchy;
};

export const addDataTypeToHierarchy = (hierarchy, dataType) =>
    hierarchy.map((condition) => {
        const { level0_parent_id, level1_parent_id, level2_parent_id, level3_parent_id } =
            condition;

        const prefix = getDataTypeLocalePrefix(dataType);

        return {
            ...condition,
            data_type: dataType,
            level0_parent_id: `${prefix}${level0_parent_id}`,
            level1_parent_id: `${prefix}${level1_parent_id}`,
            level2_parent_id: `${prefix}${level2_parent_id}`,
            level3_parent_id: `${prefix}${level3_parent_id}`,
        };
    });

export const sortResponseByTimeUnit = (
    response: DataResponse,
    timeUnitKey: TimeUnitKey,
    shouldCastTimeUnitsToString?: boolean
): DataResponse => {
    if (!response) {
        return { columns: [], records: [] };
    }
    const { columns, records } = response;
    const timeUnitKeyIdx = columns.indexOf(timeUnitKey);
    const sortedRecords = sortBy(timeUnitKeyIdx, records);
    if (shouldCastTimeUnitsToString) {
        sortedRecords.forEach((record) => {
            record[timeUnitKeyIdx] = record[timeUnitKeyIdx].toString();
        });
    }
    return {
        columns,
        records: sortedRecords,
    };
};

const filterLocalePrefixMap = {
    [AGE_GROUP_ID_KEY]: 'age_group_',
    [CAUSE_ID_KEY]: 'cause_',
    [COVARIATE_ID_KEY]: 'covariate_',
    [SDOH_ID_KEY]: 'sdoh_',
    [FORECAST_SCENARIO_ID_KEY]: 'forecast_scenario_',
    [GENDER_ID_KEY]: 'full_gender_',
    [LOCATION_ID_KEY]: 'location_',
    [MEASURE_ID_KEY]: 'measure_',
    [METRIC_ID_KEY]: 'long_metric_',
    [REI_ID_KEY]: 'rei_',
    [RISK_EXPOSURE_ID_KEY]: 'risk_exposure_',
    [ROUND_ID_KEY]: 'round_',
    [SEQUELA_ID_KEY]: 'sequela_',
    [COMORBID_CAUSE_ID_KEY]: 'cause_',
    [RACE_ID_KEY]: 'race_',
    [ANTIBIOTIC_CLASS_ID_KEY]: 'antibiotic_class_',
    [PATHOGEN_ID_KEY]: 'pathogen_',
    [INFECTIOUS_SYNDROME_ID_KEY]: 'infectious_syndrome_',
    [COUNTERFACTUAL_ID_KEY]: 'counterfactual_',
    [PRIMARY_ENTITY_ID_LOCALIZATION_KEY]: '',
    [RESPONSE_ID_KEY]: 'response_',
    [EDUCATION_ID_KEY]: 'education_',
    [CASE_SEVERITY_ID_KEY]: 'case_severity_',
    [CASE_HOSPITALIZATION_ID_KEY]: 'case_hospitalization_',
    [CASE_OUTCOME_ID_KEY]: 'case_outcome_',

    [INFECTION_TYPE_ID_KEY]: 'infection_type_',
    [SEROTYPE_ID_KEY]: 'serotype_',
    [VACCINATION_STATUS_ID_KEY]: 'vaccination_status_',
    [WOLBACHIA_INCLUDED_ID_KEY]: 'wolbachia_included_',
    [PAYER_ID_KEY]: 'payer_',
    [TOC_ID_KEY]: 'toc_',
};

// TODO: it's not clear why below was added, what was the requirement
const getFilterLocalePrefixMap = (dataType: DataType | ''): object => {
    const baseMap = filterLocalePrefixMap;
    const dataTypeSpecificMap =
        dataType === SevRecords
            ? {
                  [METRIC_ID_KEY]: 'sev_records_long_metric_',
              }
            : dataType === HealthThreatsMetricsRecords
              ? {
                    [MEASURE_ID_KEY]: 'ght_records_measure_',
                }
              : {};

    return {
        ...baseMap,
        ...dataTypeSpecificMap,
    };
};

export const localizeFilterValues = (
    filter: string,
    values: number[],
    dataType: DataType | '' = ''
): string => {
    const prefixMap = getFilterLocalePrefixMap(dataType);
    const prefix = prefixMap[filter];
    return (values || []).map((value) => _(`${prefix}${value}`)).join(', ');
};

export const getChartTitle = (
    conditionTypes: DataType[],
    localizedSelectedConditions: string, // @todo Better API would be selectionConditionsGranularity {cause_id: [...], sequela_id: [...]}
    selectedFilterValues: DataGranularity,
    granularityFiltersAvailableForSelectedConditions: DataGranularityKey[] | string[],
    titleFilters: DataGranularityKey[],
    timelineValue: string | number,
    chartType?: ChartType
): string => {
    const filters = intersection(
        titleFilters,
        Object.keys(selectedFilterValues),
        granularityFiltersAvailableForSelectedConditions
    );

    const isCovid19Data = conditionTypes?.includes(Covid19Records);
    const isRisksExposureData = conditionTypes?.includes(RiskExposureRecords);
    const isLifeExpectancyData = conditionTypes?.includes(LifeExpectancyRecords);
    const isDengueData = conditionTypes?.includes(TakedaDengueRecords);
    const isComorbidityData = areComorbidityDataTypes(conditionTypes);
    const isGHTData = conditionTypes?.includes(HealthThreatsMetricsRecords);

    let titleParts: string[] = [];

    // For Covid-19 data unit should be first
    if (isCovid19Data && filters?.includes(METRIC_ID_KEY) && selectedFilterValues[METRIC_ID_KEY]) {
        titleParts.push(
            localizeFilterValues(
                METRIC_ID_KEY,
                selectedFilterValues[METRIC_ID_KEY],
                conditionTypes[0]
            )
        );
        pull(filters, METRIC_ID_KEY);
    }

    // If we have conditions
    if (localizedSelectedConditions) {
        // If we display REI data types
        if (areReiDataTypes(conditionTypes)) {
            const hasCauses = !isEmpty(selectedFilterValues[CAUSE_ID_KEY]);
            if (hasCauses) {
                titleParts.push(
                    localizeFilterValues(
                        CAUSE_ID_KEY,
                        selectedFilterValues[CAUSE_ID_KEY],
                        conditionTypes[0]
                    )
                );
                titleParts.push('due to');
            }
            titleParts.push(localizedSelectedConditions);
            if (!isEmpty(intersection(conditionTypes, [ImpairmentRecords, InjuryRecords]))) {
                titleParts.reverse();
            }
            pull(filters, CAUSE_ID_KEY);
        } else if (isDengueData) {
            titleParts.push(_(TakedaDengueRecords));
        }
        // If we have conditions Risk Exposure
        else if (isRisksExposureData) {
            titleParts.push(
                localizeFilterValues(
                    RISK_EXPOSURE_ID_KEY,
                    selectedFilterValues[RISK_EXPOSURE_ID_KEY],
                    conditionTypes[0]
                )
            );
            pull(filters, RISK_EXPOSURE_ID_KEY);
        } else if (!isLifeExpectancyData) {
            titleParts.push(localizedSelectedConditions);
        }
    }
    // If we have only data types
    else {
        const localizedSelectedTypes = (conditionTypes || [])
            .map((dataType) => _(`data_type_${dataType}`))
            .join(', ');

        // If we display REI data types
        if (areReiDataTypes(conditionTypes)) {
            titleParts.push(localizedSelectedTypes);
            titleParts.push('map for');
            titleParts.push(
                localizeFilterValues(
                    CAUSE_ID_KEY,
                    selectedFilterValues[CAUSE_ID_KEY],
                    conditionTypes[0]
                )
            );
            pull(filters, CAUSE_ID_KEY);
        } else {
            titleParts.push(localizedSelectedTypes);
        }
    }

    // Remove condition primary entity ids from the rest of the filters
    for (const dataType of conditionTypes) {
        if (dataTypeHasPrimaryEntity(dataType)) {
            pull(filters, getDataTypePrimaryEntityKey(dataType));
        }
    }

    for (const filter of filters) {
        if (
            filter === MEASURE_ID_KEY &&
            selectedFilterValues[filter].includes(populationMeasureId) &&
            conditionTypes.includes(PopulationRecords)
        ) {
            continue;
        }
        titleParts.push(
            localizeFilterValues(filter, selectedFilterValues[filter], conditionTypes[0])
        );
    }

    titleParts.push('in');
    let dateRange = timelineValue.toString();
    if (isDengueData) {
        titleParts.push('Year');
        dateRange = dateRange.replace(' to ', ' - ');
    }
    titleParts.push(dateRange);

    // Comorbidity tool
    if (isComorbidityData) {
        titleParts = [];
        const comorbidCauseName = localizeFilterValues(
            COMORBID_CAUSE_ID_KEY,
            selectedFilterValues[COMORBID_CAUSE_ID_KEY],
            conditionTypes[0]
        );

        if (areComorbidityCoprevalenceDataTypes(conditionTypes)) {
            const metricId = selectedFilterValues[METRIC_ID_KEY][0];
            if (metricId === 1) {
                // Number unit
                titleParts.push(
                    `Number of prevalent cases of ${comorbidCauseName} among individuals with ${localizedSelectedConditions} in ${timelineValue}`
                );
            } else if (metricId === 2) {
                // Number unit
                titleParts.push(
                    `Prevalence (%) of ${comorbidCauseName} among individuals with ${localizedSelectedConditions} in ${timelineValue}`
                );
            } else {
                // Rate unit
                titleParts.push(
                    `Prevalence of ${comorbidCauseName} per 100,000 cases of ${localizedSelectedConditions} in ${timelineValue}`
                );
            }
        }
        // projected_comorbidity_causes_outcome_records data type
        else {
            titleParts.push(
                `Projected new cases of ${comorbidCauseName} per 100,000 cases of ${localizedSelectedConditions} over a 12-month period`
            );
        }
    }

    if (isGHTData && chartType === TYPE_SCATTER_MAP_CHART) {
        titleParts = [`Highest threat by location as of ${timelineValue}`];
    }

    return capitalize(titleParts.filter((v) => v !== '').join(' '));
};

const capitalize = (value: string): string => value[0].toUpperCase() + value.slice(1);

export const getChartSubTitle = (
    selectedFilterValues: DataGranularity,
    granularityFiltersAvailableForSelectedConditions: DataGranularityKey[] | string[],
    titleFilters: DataGranularityKey[],
    dataTypes: DataType[] | null = null,
    ageGroupEntities: AgeGroupEntity[] | null = null
): string => {
    let filters;

    if (dataTypes && areComorbidityDataTypes(dataTypes) && ageGroupEntities) {
        // Comorbidity tool
        filters = [LOCATION_ID_KEY, GENDER_ID_KEY];
        if (dataTypes.includes(CauseAssociatedComorbidityStudyCoefficients)) {
            filters.push(AGE_GROUP_ID_KEY);
        }

        return filters
            .map((filter) =>
                filter === AGE_GROUP_ID_KEY
                    ? getAgeGroupsRange(selectedFilterValues[filter], ageGroupEntities)
                    : localizeFilterValues(filter, selectedFilterValues[filter])
            )
            .join(', ');
    }

    filters =
        intersection(
            titleFilters,
            Object.keys(selectedFilterValues),
            granularityFiltersAvailableForSelectedConditions
        ) || [];

    return filters
        .map((filter) => localizeFilterValues(filter, selectedFilterValues[filter]))
        .join(', ');
};

export const getYAxisTitle = (
    selectedFilterValues: DataGranularity,
    dataType: DataType
): string => {
    if (selectedFilterValues[METRIC_ID_KEY]) {
        return localizeFilterValues(METRIC_ID_KEY, selectedFilterValues[METRIC_ID_KEY], dataType);
    }

    if (selectedFilterValues[MEASURE_ID_KEY]) {
        return localizeFilterValues(MEASURE_ID_KEY, selectedFilterValues[MEASURE_ID_KEY], dataType);
    }

    return '';
};

export const chartProviderFilterValueToArray = (value: ChartProviderFilterValue): number[] =>
    value
        .toString()
        .split(',')
        .map((v) => parseInt(v));

export const chartProviderFiltersToObject = (
    chartProviderFilters: ChartProviderFilters
): object => {
    const result = {};

    for (const filter in chartProviderFilters) {
        if (chartProviderFilters[filter]) {
            result[filter] = chartProviderFilterValueToArray(chartProviderFilters[filter]).map(
                (id) => parseInt(id)
            );
        }
    }

    return result;
};

export const getLeadingDataTypeInMultiTypeSelections = (dataTypes: DataType[]): DataType => {
    if (dataTypes.includes(CauseOutcome) && dataTypes.includes(SequelaOutcome)) {
        return CauseOutcome;
    }

    return dataTypes[0];
};
